import {
  ContextType,
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";

import {
  ConnectionBaseFragment,
  SyncListItemFragment,
  useActivateConnectionSettingsLazyQuery,
} from "@/apollo/types";
import { useCallbackRef } from "@/hooks/useCallbackRef";

import { useCreateSync } from "../useCreateSync";
import {
  ReverseETLStateContext,
  ReverseETLStepState,
  createInitialState,
} from "./StepStateContext";

const ReverseETLSetupContext = createContext<{
  connectionHasSettings: boolean | undefined;
  isLoadingConnectionSettings: boolean;
  onSetupStarted: (connection: ConnectionBaseFragment) => void;
  createSync: (state: ReverseETLStepState) => Promise<void>;
  getConnectionSettings: (connectionId: string) => Promise<{
    connectionSettings?: Record<string, any>;
    error?: Error | undefined;
  }>;
} | null>(null);

export function useReverseETLSetupContext() {
  const ctx = useContext(ReverseETLSetupContext);
  if (!ctx) {
    throw new Error(
      "useReverseETLStepsContext must be used within a ReverseETLStepsContext.Provider",
    );
  }
  return ctx;
}

export function ReverseETLSetupProvider(props: {
  integrationId?: string;
  connection?: ConnectionBaseFragment;
  connectionSettings?: Record<string, any>;
  children: ReactNode;
  onSetupStarted: (connection: ConnectionBaseFragment) => void;
  onSetupCompleted: (sync: SyncListItemFragment) => void;
  onSetupError?: (error: Error, state: ReverseETLStepState) => void;
}) {
  const [state, setState] = useState(
    createInitialState({
      integrationId: props.integrationId,
      connection: props.connection,
    }),
  );

  const onSetupCompleted = useCallbackRef(props.onSetupCompleted);
  const onSetupError = useCallbackRef(props.onSetupError);

  const { createSync } = useCreateSync({
    onSyncCreated(sync) {
      onSetupCompleted(sync);
    },
    onSyncError(error) {
      onSetupError(error, state);
    },
  });

  const [
    fetchConnectionSettings,
    { data: connectionSettingsData, loading: isLoadingConnectionSettings },
  ] = useActivateConnectionSettingsLazyQuery();

  const connectionId = state.destination.value?.connection?.id;

  const connectionHasSettings =
    !connectionId || isLoadingConnectionSettings
      ? undefined
      : !!Object.keys(
          connectionSettingsData?.getActivateSettings.connectionSettings ||
            props.connectionSettings ||
            {},
        ).length;

  const getConnectionSettings = useCallback(
    async (connectionId: string) => {
      try {
        const { data, error } = await fetchConnectionSettings({
          variables: {
            input: {
              id: connectionId,
              formValues: {},
            },
          },
        });
        return {
          connectionSettings: data?.getActivateSettings.connectionSettings,
          error,
        };
      } catch (error) {
        return {
          connectionSettings: undefined,
          error: error instanceof Error ? error : new Error("Unknown error"),
        };
      }
    },
    [fetchConnectionSettings],
  );

  useEffect(() => {
    if (connectionId) {
      getConnectionSettings(connectionId);
    }
  }, [connectionId, getConnectionSettings]);

  const stepsContext: ContextType<typeof ReverseETLSetupContext> = {
    connectionHasSettings,
    isLoadingConnectionSettings,
    onSetupStarted: props.onSetupStarted,
    createSync,
    getConnectionSettings,
  };

  return (
    <ReverseETLStateContext.Provider value={{ state, setState }}>
      <ReverseETLSetupContext.Provider value={stepsContext}>
        {props.children}
      </ReverseETLSetupContext.Provider>
    </ReverseETLStateContext.Provider>
  );
}
